import React, { useMemo } from 'react';
import { Loader2 } from 'lucide-react';

const TYPE_STYLES = {
  lead: { bg: 'bg-sky-500', tag: 'bg-sky-100 text-sky-700 border-sky-300', label: 'Lead' },
  engagement: {
    bg: 'bg-teal-500',
    tag: 'bg-teal-100 text-teal-700 border-teal-300',
    label: 'Engagement'
  },
  site_visit: {
    bg: 'bg-indigo-500',
    tag: 'bg-indigo-100 text-indigo-700 border-indigo-300',
    label: 'Site Visit'
  },
  pipeline: {
    bg: 'bg-violet-500',
    tag: 'bg-violet-100 text-violet-700 border-violet-300',
    label: 'Pipeline'
  },
  booking: {
    bg: 'bg-emerald-500',
    tag: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    label: 'Booking'
  },
  dropped: {
    bg: 'bg-rose-500',
    tag: 'bg-rose-100 text-rose-700 border-rose-300',
    label: 'Dropped'
  },
  payment: {
    bg: 'bg-amber-500',
    tag: 'bg-amber-100 text-amber-800 border-amber-300',
    label: 'Payment'
  },
  follow_up: {
    bg: 'bg-orange-500',
    tag: 'bg-orange-100 text-orange-700 border-orange-300',
    label: 'Follow-up'
  }
};

const DEFAULT_TYPE = {
  bg: 'bg-[#2A565E]',
  tag: 'bg-gray-100 text-gray-700 border-gray-300',
  label: 'Activity'
};

const formatActivityTime = (activity) => {
  if (activity?.occurredAt) {
    const at = new Date(activity.occurredAt);
    if (!Number.isNaN(at.getTime())) {
      return at.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata'
      });
    }
  }
  return activity?.displayTime || '';
};

const formatSyncedLabel = (syncedAt) => {
  if (!syncedAt) return null;
  const at = new Date(syncedAt);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata'
  });
};

/**
 * Latest-first sales activity timeline. Parent owns polling; this stays scrollable.
 */
const SalesActivityFeed = ({
  activities = [],
  isLoading = false,
  isFetching = false,
  showProjectTags = false,
  hasProjects = true,
  error = null,
  syncedAt = null,
  scrollable = true,
  className = ''
}) => {
  const sortedActivities = useMemo(
    () =>
      [...(activities || [])].sort(
        (a, b) =>
          new Date(b.occurredAt || 0).getTime() - new Date(a.occurredAt || 0).getTime()
      ),
    [activities]
  );

  const syncedLabel = formatSyncedLabel(syncedAt);
  const showInitialSpinner = isLoading && sortedActivities.length === 0;

  const metaBar = (
    <div className="mb-2 flex items-center justify-between gap-2 px-0.5">
      <p className="text-[11px] text-gray-500">
        Latest first
        {syncedLabel ? ` · Updated ${syncedLabel}` : ''}
        <span className="text-gray-400"> · Auto every 5 min</span>
      </p>
      {isFetching && !showInitialSpinner ? (
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-gray-400" aria-label="Refreshing" />
      ) : null}
    </div>
  );

  if (!hasProjects) {
    return (
      <div className={className}>
        {metaBar}
        <div className="text-center py-8 px-3">
          <div className="mx-auto mb-3 h-10 w-10 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 7h18M3 12h12M3 17h8"
              />
            </svg>
          </div>
          <p className="text-sm font-medium text-gray-700">No projects available</p>
          <p className="text-xs text-gray-500 mt-1">
            Add a project to start seeing today&apos;s sales activity.
          </p>
        </div>
      </div>
    );
  }

  if (showInitialSpinner) {
    return (
      <div className={className}>
        {metaBar}
        <div className="flex items-center justify-center py-10">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#0A4A5C] border-t-transparent" />
        </div>
      </div>
    );
  }

  if (error && sortedActivities.length === 0) {
    return (
      <div className={className}>
        {metaBar}
        <div className="text-center py-8 px-3">
          <p className="text-sm font-medium text-gray-700">Couldn&apos;t load sales activity</p>
          <p className="text-xs text-gray-500 mt-1">
            Auto-refresh will retry shortly. Please check back in a moment.
          </p>
        </div>
      </div>
    );
  }

  if (!sortedActivities.length) {
    return (
      <div className={className}>
        {metaBar}
        <div className="text-center py-8 px-3">
          <div className="mx-auto mb-3 h-10 w-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <p className="text-sm font-medium text-gray-700">No sales activity today</p>
          <p className="text-xs text-gray-500 mt-1">
            New leads, visits, and bookings will show up here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex min-h-0 flex-col ${className}`}>
      {metaBar}
      <div
        className={
          scrollable
            ? 'min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1'
            : ''
        }>
        <ol className="relative space-y-3">
          {/* 1px rail centered on the 20px avatar (left + half width). */}
          <div
            className="pointer-events-none absolute bottom-3 left-2.5 top-2.5 w-px -translate-x-1/2 bg-gray-200"
            aria-hidden
          />
          {sortedActivities.map((activity, index) => {
            const style = TYPE_STYLES[activity.type] || DEFAULT_TYPE;
            const initial = (activity.title || style.label).charAt(0).toUpperCase();
            const timeLabel = formatActivityTime(activity);
            const isNewest = index === 0;

            return (
              <li key={activity.id} className="relative flex gap-3">
                <span
                  className={`relative z-10 mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white ring-2 ring-white ${style.bg}`}>
                  {initial}
                </span>
                <div
                  className={`min-w-0 flex-1 rounded-lg border px-2.5 py-2 transition-colors ${
                    isNewest
                      ? 'border-teal-200 bg-teal-50/40'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-gray-800 leading-snug">
                      {activity.title}
                    </p>
                    {timeLabel ? (
                      <span className="shrink-0 text-[11px] font-semibold tabular-nums text-gray-500 whitespace-nowrap">
                        {timeLabel}
                      </span>
                    ) : null}
                  </div>
                  {(activity.detail || activity.primary) && (
                    <p className="mt-0.5 text-xs text-gray-500 leading-snug break-words">
                      {activity.detail || activity.primary}
                    </p>
                  )}
                  <div className="mt-1.5 flex flex-wrap items-center gap-1">
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${style.tag}`}>
                      {style.label}
                    </span>
                    {showProjectTags && activity.projectName ? (
                      <span className="inline-flex items-center rounded-full border border-gray-300 bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-700">
                        {activity.projectName}
                      </span>
                    ) : null}
                    {isNewest ? (
                      <span className="inline-flex items-center rounded-full border border-teal-300 bg-teal-100 px-2 py-0.5 text-[10px] font-semibold text-teal-800">
                        Latest
                      </span>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
};

export default SalesActivityFeed;
